// shell/components/entity-fields/form-renderers/number-input.ts

import { createElement } from '../../../utils/dom';
import type { NumberFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a number input.
 */
export function renderNumberInput<T>(
    field: NumberFormField<T>,
    initialValue: unknown,
    onChange: (value: number | null) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--number',
    });

    const inputEl = createElement('input', {
        className: 'ev-input ev-input--number',
        attributes: {
            type: 'number',
            ...(field.placeholder ? { placeholder: field.placeholder } : {}),
            ...(field.min !== undefined ? { min: String(field.min) } : {}),
            ...(field.max !== undefined ? { max: String(field.max) } : {}),
            ...(field.step !== undefined ? { step: String(field.step) } : {}),
            ...(field.disabled ? { disabled: '' } : {}),
            ...(field.required ? { 'aria-required': 'true' } : {}),
        },
    }) as HTMLInputElement;

    // Set initial value
    if (initialValue != null && initialValue !== '') {
        inputEl.value = String(initialValue);
    }

    // Handle change
    const handleInput = () => {
        const value = inputEl.value === '' ? null : parseFloat(inputEl.value);
        onChange(isNaN(value as number) ? null : value);
    };
    inputEl.addEventListener('input', handleInput);

    container.appendChild(inputEl);

    return {
        element: container,
        getValue: () => {
            if (inputEl.value === '') return null;
            const num = parseFloat(inputEl.value);
            return isNaN(num) ? null : num;
        },
        setValue: (value: unknown) => {
            if (value == null || value === '') {
                inputEl.value = '';
            } else {
                inputEl.value = String(value);
            }
        },
        validate: async () => true,
        focus: () => inputEl.focus(),
        dispose: () => {
            inputEl.removeEventListener('input', handleInput);
        },
    };
}
